import type { DadosSindicatoDetalhe } from "../../../../../services";
import type { SecaoDocumento } from "../../../../../domain/shipping/models";

export type LinhaResumoEnvioV2 =
  | { kind: "text"; text: string }
  | { kind: "perguntaResposta"; pergunta: string; resposta: string };

export type BlocoResumoEnvioV2 = { titulo: string; linhas: LinhaResumoEnvioV2[] };

export function textoLinhaResumo(l: LinhaResumoEnvioV2): string {
  if (l.kind === "perguntaResposta") {
    return `${l.pergunta}\nResposta: ${l.resposta}`;
  }
  return l.text;
}

export function linhasDaSecaoResumo(secao: SecaoDocumento): LinhaResumoEnvioV2[] {
  const linhas: LinhaResumoEnvioV2[] = [];

  if (secao.tipo === "somente-analista") {
    return linhas;
  }

  if (secao.id === "adimplencia" && secao.adimplenciaMeta?.initial) {
    const i = secao.adimplenciaMeta.initial;
    linhas.push({
      kind: "text",
      text: `Forma de cobrança: ${i.formaCobrancaValorUI?.trim() || "—"}`,
    });
    linhas.push({
      kind: "text",
      text: `Quantidade de empresas associadas: ${i.quantidadeAssociados || "—"}`,
    });
    linhas.push({
      kind: "text",
      text: `Tipo de documento: ${i.tipoDocumentoValorUI?.trim() || "—"}`,
    });
    if (i.observacaoGeral?.trim()) {
      linhas.push({ kind: "text", text: `Observação geral: ${i.observacaoGeral.trim()}` });
    }
    const n = Math.max(0, i.quantidadeAssociados || 0);
    for (let j = 0; j < n; j++) {
      const a = i.associados[j];
      if (!a) continue;
      linhas.push({
        kind: "text",
        text: `Associado ${j + 1}: CNPJ ${a.cnpj?.trim() || "—"} — ${a.nomeSocial?.trim() || "—"}`,
      });
      linhas.push({
        kind: "text",
        text: `  Arquivo: ${a.arquivoNomeExibicao?.trim() || a.arquivoNome?.trim() || "(sem arquivo)"}`,
      });
      if (a.observacao?.trim()) {
        linhas.push({ kind: "text", text: `  Observação: ${a.observacao.trim()}` });
      }
    }
    return linhas;
  }

  if (secao.pergunta?.trim()) {
    const resp = secao.initialPerguntaValor?.trim() || "—";
    linhas.push({
      kind: "perguntaResposta",
      pergunta: secao.pergunta.trim(),
      resposta: resp,
    });
  }

  if (secao.tipo === "upload" || secao.tipo === "pergunta-com-upload") {
    const isNao = String(secao.initialPerguntaValor || "").toUpperCase() === "NÃO";
    if (secao.tipo === "pergunta-com-upload" && isNao) {
      linhas.push({
        kind: "text",
        text: "Arquivo / comprovante: não se aplica (opção NÃO)",
      });
    } else {
      linhas.push({
        kind: "text",
        text: `Arquivo: ${secao.initialArquivoNomeExibicao?.trim() || secao.initialArquivoName?.trim() || "(sem arquivo)"}`,
      });
    }
  }

  if (secao.initialObservacao?.trim()) {
    linhas.push({ kind: "text", text: `Observação: ${secao.initialObservacao.trim()}` });
  }

  if (linhas.length === 0) {
    linhas.push({ kind: "text", text: "(Sem detalhes adicionais neste critério.)" });
  }

  return linhas;
}

export function montarBlocosResumoDocumentacao(
  tituloGrupo: string,
  secoes: SecaoDocumento[],
): BlocoResumoEnvioV2[] {
  if (!secoes.length) return [];
  return secoes.map((s) => ({
    titulo: `${tituloGrupo} — ${s.titulo}`,
    linhas: linhasDaSecaoResumo(s),
  }));
}

export function montarTextoCompletoResumoDocumentacao(
  dados: DadosSindicatoDetalhe | null,
  blocos: BlocoResumoEnvioV2[],
): string {
  const lines: string[] = [];
  lines.push("RESUMO DA DOCUMENTAÇÃO — MERITOCRACIA");
  lines.push("");

  if (dados) {
    lines.push("DADOS DO SINDICATO (CADASTRO)");
    lines.push("—".repeat(32));
    const nome = dados.nomeSindicato ?? dados.nome;
    if (nome) lines.push(`Nome: ${nome}`);
    if (dados.cnpj) lines.push(`CNPJ: ${dados.cnpj}`);
    if (dados.sigla) lines.push(`Sigla: ${dados.sigla}`);
    if (dados.regional) lines.push(`Regional: ${dados.regional}`);
    if (dados.grupo) lines.push(`Grupo: ${dados.grupo}`);
    const pres = dados.presidente ?? dados.nomePresidente;
    if (pres) lines.push(`Presidente: ${pres}`);
    if (dados.emailSindicato) lines.push(`E-mail: ${dados.emailSindicato}`);
    if (dados.telefoneSindicato) lines.push(`Telefone: ${dados.telefoneSindicato}`);
    const coord = dados.nomeCoordenador ?? dados.nomeCordenador;
    const telCoord = dados.telefoneCoordenador ?? dados.telefoneCordenador;
    if (coord) lines.push(`Coordenador(a): ${coord}`);
    if (telCoord) lines.push(`Telefone coord.: ${telCoord}`);
    if (dados.responsavelPreenchimento) {
      lines.push(`Responsável pelo preenchimento: ${dados.responsavelPreenchimento}`);
    }
    if (dados.cargoResponsavel) {
      lines.push(`Cargo: ${dados.cargoResponsavel}`);
    }
    lines.push("");
  }

  blocos.forEach((b) => {
    lines.push(b.titulo.toUpperCase());
    lines.push("-".repeat(Math.min(b.titulo.length, 48)));
    b.linhas.forEach((l) => {
      const t = textoLinhaResumo(l);
      t.split("\n").forEach((part) => lines.push(part));
    });
    lines.push("");
  });

  return lines.join("\n");
}

export function blocosResumoDasSecoes(
  secoesObrigatorios: SecaoDocumento[],
  secoesBonus: SecaoDocumento[],
  secoesBonusExtra: SecaoDocumento[],
): BlocoResumoEnvioV2[] {
  return [
    ...montarBlocosResumoDocumentacao("Documentos obrigatórios", secoesObrigatorios),
    ...montarBlocosResumoDocumentacao("Documentos bônus", secoesBonus),
    ...montarBlocosResumoDocumentacao("Documentos bônus extra", secoesBonusExtra),
  ];
}
